// GT.fmt — the single formatting utility for Graham Terminal.
//
// Replaces the ~22 per-file `fmt`/fmtMoney/fmtPct/fmtPrice/fmtAum definitions
// scattered across the frontend. All formatters return the em-dash '—' for
// null/undefined so callers never special-case missing data.
//
// Two percent flavours on purpose, because the codebase has both conventions:
//   • pct(0.015)        -> "+1.50%"   (input is a FRACTION, multiplied by 100)
//   • pctPoints(1.5)    -> "+1.50%"   (input is already in PERCENTAGE POINTS)
// Match whichever the old call site used when migrating.

const DASH = '—';

function isNum(v) {
  return v != null && v !== '' && !Number.isNaN(+v);
}

// Compact currency: $1.23T / $45.6B / $789.0M / $12K / $340
function money(v) {
  if (!isNum(v)) return DASH;
  const n = +v;
  const abs = Math.abs(n);
  const sign = n < 0 ? '−' : '';
  if (abs >= 1e12) return sign + '$' + (abs / 1e12).toFixed(2) + 'T';
  if (abs >= 1e9) return sign + '$' + (abs / 1e9).toFixed(1) + 'B';
  if (abs >= 1e6) return sign + '$' + (abs / 1e6).toFixed(1) + 'M';
  if (abs >= 1e3) return sign + '$' + (abs / 1e3).toFixed(0) + 'K';
  return sign + '$' + abs.toFixed(0);
}

// Compact plain number (no $): 1.23T / 45.6B / 789M / 12.3K / 340
function compact(v) {
  if (!isNum(v)) return DASH;
  const n = +v;
  const abs = Math.abs(n);
  const sign = n < 0 ? '−' : '';
  if (abs >= 1e12) return sign + (abs / 1e12).toFixed(2) + 'T';
  if (abs >= 1e9) return sign + (abs / 1e9).toFixed(1) + 'B';
  if (abs >= 1e6) return sign + (abs / 1e6).toFixed(1) + 'M';
  if (abs >= 1e3) return sign + (abs / 1e3).toFixed(1) + 'K';
  return sign + abs.toFixed(0);
}

// Full grouped dollars, no decimals: $1,234,567 (portfolio values, cash).
function dollars(v) {
  if (!isNum(v)) return DASH;
  return '$' + Math.round(+v).toLocaleString('en-US');
}

// AUM display used by the global search (nav.js): $1.2B / $340M / $12,345
function aum(v) {
  if (!isNum(v)) return DASH;
  const n = +v;
  // Tier on the MAGNITUDE and re-apply the sign: testing `n >= 1e9` directly
  // sent every negative straight to the locale branch, so a -$2.1B rendered as
  // '$-2,100,000,000' while a positive one rendered as '$2.1B'.
  const sign = n < 0 ? '−' : '';
  const a = Math.abs(n);
  if (a >= 1e9) return sign + '$' + (a / 1e9).toFixed(1) + 'B';
  if (a >= 1e6) return sign + '$' + (a / 1e6).toFixed(0) + 'M';
  return sign + '$' + a.toLocaleString('en-US');
}

// Exact price: $123.45
function price(v, digits = 2) {
  if (!isNum(v)) return DASH;
  return '$' + (+v).toFixed(digits);
}

// Signed percent from a FRACTION (0.015 -> "+1.50%")
function pct(v, digits = 2) {
  if (!isNum(v)) return DASH;
  const p = +v * 100;
  return (p > 0 ? '+' : '') + p.toFixed(digits) + '%';
}

// Signed percent from PERCENTAGE POINTS (1.5 -> "+1.50%")
function pctPoints(v, digits = 2) {
  if (!isNum(v)) return DASH;
  const p = +v;
  return (p > 0 ? '+' : '') + p.toFixed(digits) + '%';
}

// Unsigned percent from a fraction (0.015 -> "1.50%")
function pctPlain(v, digits = 2) {
  if (!isNum(v)) return DASH;
  return (+v * 100).toFixed(digits) + '%';
}

// Fixed-decimal number: 12.34
function num(v, digits = 2) {
  if (!isNum(v)) return DASH;
  return (+v).toFixed(digits);
}

// Thousands-separated integer: 1,234,567
function int(v) {
  if (!isNum(v)) return DASH;
  return Math.round(+v).toLocaleString('en-US');
}

// Semantic direction class helper: 'up' | 'down' | 'flat' for a signed value.
function dir(v) {
  if (!isNum(v)) return 'flat';
  return +v > 0 ? 'up' : +v < 0 ? 'down' : 'flat';
}

export const fmt = {
  DASH,
  money,
  compact,
  dollars,
  aum,
  price,
  pct,
  pctPoints,
  pctPlain,
  num,
  int,
  dir,
};

export default fmt;
